import type { BadgeRecipeProps } from '../recipes/badge.recipe';

export type StatusTone = NonNullable<BadgeRecipeProps['tone']>;

/**
 * Maps a domain status onto a badge tone in one place, so "completed" is the
 * same green on the call log, the driver profile and the invoice list.
 *
 * Add new statuses here rather than picking a tone at the call site — that is
 * what keeps the colours consistent as the API grows. Keys are lowercase with
 * hyphens; StatusBadge normalises spaces and underscores before looking up.
 */
export const statusTone: Record<string, StatusTone> = {
  // Positive, finished
  completed: 'success',
  complete: 'success',
  paid: 'success',
  active: 'success',
  online: 'success',
  released: 'success',
  approved: 'success',
  // The sales AI wrote the prospect a page.
  built: 'success',

  // In progress
  'in-progress': 'info',
  // The system is working on it, rather than a person. Purple keeps a running
  // import distinct from a fact about the record, which is what info means.
  processing: 'processing',
  // Driver states that are a job phase rather than a severity. These get the
  // phase tones so a driver reading "towing" on the dashboard sees the same
  // colour the map uses for that leg of the job.
  //
  // Every leg of a call takes its phase, as the map and the phone's Figma
  // colour it: dispatched, en route and on scene orange, towing blue.
  // Dispatched was the brand green, a shade off Completed's, and on scene
  // navy (the owner, 2026-10-29).
  dispatched: 'phase-dispatched',
  'en-route': 'phase-en-route',
  enroute: 'phase-en-route',
  'on-scene': 'phase-on-scene',
  towing: 'phase-towing',
  returning: 'phase-returning',
  assigned: 'info',
  // A call the partner network has accepted but not yet dispatched a driver to.
  'accepted-awaiting': 'info',
  // A partner network's call put to the company, not yet taken.
  offered: 'info',
  // Done on the road, waiting on the motor club to say so.
  'completed-awaiting': 'success',
  // Gone on arrival: the job ended with no tow. Not a failure of ours.
  goa: 'neutral',
  'goa-awaiting': 'neutral',
  // The partner network's offer, turned down.
  declined: 'destructive',

  // Waiting on something
  //
  // `pending` is info rather than warning: the dispatch screens have shown it
  // blue since before this table existed, and a pending call is not something
  // anyone has to act on — it is simply not started. Two screens rendering the
  // same call in two colours is worse than either choice.
  pending: 'info',
  waiting: 'warning',
  // An invoice that has gone out but not been paid — awaiting someone else.
  // Also a share recipient whose link went out and has not been opened.
  sent: 'warning',
  unassigned: 'warning',
  overdue: 'warning',
  'partially-paid': 'warning',

  // Failed or stopped
  cancelled: 'destructive',
  canceled: 'destructive',
  failed: 'destructive',
  rejected: 'destructive',
  void: 'destructive',
  // A step the system gave up on, such as the sales AI after three tries.
  error: 'destructive',

  // No particular signal
  draft: 'neutral',
  clear: 'info',
  'on-leave': 'neutral',
  inactive: 'destructive',
  // A call that has come in but has not been acted on yet.
  new: 'neutral',
  offline: 'neutral',
  archived: 'neutral',
  // The sales AI judged a call not worth a page. A verdict, not a failure.
  skipped: 'neutral',
  // A share recipient who opened their link.
  viewed: 'success',
  // A share recipient whose access was taken away.
  revoked: 'destructive',
};

/**
 * The tone for a status string from the API: "EN_ROUTE", "en route" and
 * "en-route" all find the same entry, and one the table does not know is
 * neutral.
 *
 * An own-property check, not a bare lookup: `statusTone['constructor']`
 * would otherwise walk the prototype chain and return a function, which `??`
 * does not catch. hasOwnProperty.call rather than Object.hasOwn, which is
 * ES2022, because the driver app runs on whatever phone a driver has.
 */
export function toneForStatus(status: string | null | undefined): StatusTone {
  const key = status?.toLowerCase().replace(/[\s_]+/g, '-') ?? '';
  return Object.prototype.hasOwnProperty.call(statusTone, key)
    ? statusTone[key]
    : 'neutral';
}
